import { eulerConfigs, eulerVaults } from '@1delta/data-sdk'
import { priceLensAbi } from '../../../abis/euler/priceLens'
import { vaultLensAbi } from '../../../abis/euler/vaultLens'
import { createMarketUid, ENABLED_EULER_V2_CHAINS } from '../../../utils'
import type {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'

/**
 * Euler V2 uses virtual addresses for fiat unit-of-account:
 *   0x0   = generic zero / unset
 *   0x348 = ISO 4217 code 840 (USD)
 * Any address <= 0xFFFF is a non-ERC-20 fiat code and treated as USD.
 */
const MAX_VIRTUAL_ADDRESS = 0xffff

/**
 * Sanity cap for per-unit USD price. Misconfigured oracles on empty
 * vaults (e.g. EULER_V2:42161:0xf8ff43f8b75c3a630e5e331613f9bdb133a49d13)
 * can report nonsensical values like $1T/unit. Any legitimate asset —
 * including WBTC, LP shares, PT tokens — stays well below this bound.
 */
const MAX_SANE_PRICE_USD = 1e7

function isUsdUnitOfAccount(addr: string): boolean {
  const numeric = parseInt(addr, 16)
  return numeric <= MAX_VIRTUAL_ADDRESS
}

/**
 * Lightweight PriceLens contract address — deployed at the same
 * address on all supported chains via CREATE2.
 */
const PRICE_LENS_ADDRESS = '0x6392892f3C57c658d2c748fc64c6fe57680E889F'

/** Chains where the PriceLens contract is deployed */
const PRICE_LENS_CHAINS: string[] = [
  '1', // Ethereum
  '56', // BSC
  '130', // Unichain
  '143', // Monad
  '146', // Sonic
  '999', // HyperEVM
  '8453', // Base
  '9745', // Plasma
  '42161', // Arbitrum
  '43114', // Avalanche
  '59144', // Linea
  '80094', // Berachain
]

/**
 * Euler V2 query metadata — one entry per vault
 */
export interface EulerV2QueryMeta {
  vault: string
  lender: string
  usePriceLens: boolean
}

/** Combined ABI covering both PriceLens and VaultLens function names */
const eulerCombinedAbi = [...priceLensAbi, ...vaultLensAbi] as const

/**
 * Build multicall calls for Euler V2 oracle prices.
 * Uses lightweight PriceLens where available, falls back to
 * VaultLens.getVaultInfoFull() on chains without it.
 */
export function getEulerV2Calls(
  chainId: string,
): FetcherResult<EulerV2QueryMeta[]>[] {
  if (!ENABLED_EULER_V2_CHAINS.includes(chainId)) return []

  const configs = eulerConfigs() ?? {}
  const vaults = eulerVaults() ?? {}
  const results: FetcherResult<EulerV2QueryMeta[]>[] = []
  const usePriceLens = PRICE_LENS_CHAINS.includes(chainId)

  for (const [lender, chainVaults] of Object.entries(vaults)) {
    const vaultList = chainVaults[chainId] ?? []
    const config = configs[lender]?.[chainId]

    if (!config || vaultList.length === 0) continue

    const calls: MulticallCall[] = []
    const meta: EulerV2QueryMeta[] = []

    for (const entry of vaultList) {
      if (usePriceLens) {
        calls.push({
          address: PRICE_LENS_ADDRESS,
          name: 'getVaultPrice',
          params: [entry.vault],
        })
      } else {
        calls.push({
          address: config.vaultLens,
          name: 'getVaultInfoFull',
          params: [entry.vault],
        })
      }

      meta.push({
        vault: entry.vault.toLowerCase(),
        lender,
        usePriceLens,
      })
    }

    if (calls.length > 0) {
      results.push({ calls, meta, lender })
    }
  }

  return results
}

/**
 * Parse Euler V2 results into oracle price entries.
 * Handles both PriceLens (flat struct) and VaultLens (nested struct) shapes.
 */
export function parseEulerV2Results(
  data: any[],
  meta: EulerV2QueryMeta[],
  context: ParseContext,
): OraclePriceEntry[] {
  const entries: OraclePriceEntry[] = []

  data.forEach((result, index) => {
    const queryMeta = meta[index]
    if (!queryMeta || !result) return

    try {
      let amountOutMid: bigint
      let unitOfAccountDecimals: bigint
      let unitOfAccount: string
      let asset: string

      if (queryMeta.usePriceLens) {
        // PriceLens: flat struct
        if (result.queryFailure) return
        amountOutMid = BigInt(result.amountOutMid)
        unitOfAccountDecimals = BigInt(result.unitOfAccountDecimals)
        unitOfAccount = (result.unitOfAccount as string).toLowerCase()
        asset = (result.asset as string).toLowerCase()
      } else {
        // VaultLens: nested struct
        const priceInfo = result.liabilityPriceInfo
        if (!priceInfo || priceInfo.queryFailure) return
        amountOutMid = BigInt(priceInfo.amountOutMid)
        unitOfAccountDecimals = BigInt(result.unitOfAccountDecimals)
        unitOfAccount = (result.unitOfAccount as string).toLowerCase()
        asset = (result.asset as string).toLowerCase()
      }

      if (amountOutMid === 0n) return

      const priceInUoA =
        Number(amountOutMid) / Number(10n ** unitOfAccountDecimals)

      if (isNaN(priceInUoA) || priceInUoA === 0) return

      // Determine USD price
      let priceUSD: number
      // A fiat unit of account (ISO 4217 virtual address) means the vault's
      // own oracle already answers in USD; a token unit of account means the
      // price hangs off that token's USD price and inherits its error.
      let derivation: 'direct' | 'quoted'

      if (isUsdUnitOfAccount(unitOfAccount)) {
        priceUSD = priceInUoA
        derivation = 'direct'
      } else {
        const uoaUSD = context.usdPrices[unitOfAccount] ?? 0
        if (uoaUSD === 0) return
        priceUSD = priceInUoA * uoaUSD
        derivation = 'quoted'
      }

      if (isNaN(priceUSD) || priceUSD === 0) return
      if (priceUSD > MAX_SANE_PRICE_USD) return

      entries.push({
        asset,
        price: priceInUoA,
        priceUSD,
        marketUid: createMarketUid(
          context.chainId,
          queryMeta.lender,
          queryMeta.vault,
        ),
        derivation,
      })
    } catch {
      // Skip invalid results
    }
  })

  return entries
}

/**
 * ABI fragments needed for Euler V2 oracle calls
 */
export function getEulerV2Abi() {
  return eulerCombinedAbi
}

/**
 * Euler V2 Oracle Fetcher module
 */
export const eulerV2Fetcher = {
  getCalls: getEulerV2Calls,
  parse: parseEulerV2Results,
  getAbi: getEulerV2Abi,
}
